import { message } from "antd";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import apiCall from "../../../components/Api";
import AntdBreadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import CommonFIlterModal from "../../../components/Common/CommonFilterModal";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import StatusComponent from "../../../components/Common/StatusComponent";
import ListPageLayout from "../../../components/Layout";
import Deletepopup from "../../../components/Modal/Deletepopup";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

const emptyFilterValues = {
  "City/Town": [],
  "Registered At": [],
};

export default function FranchiseCustomerStoreLocation() {
  const navigate = useNavigate();

  // Sorting
  const [sort, setSort] = useState("newest");
  const [sortLabel, setSortLabel] = useState("Sort by");
  const handleSortClick = useSortHandler(setSort, setSortLabel);

  // Search
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearchText, () => {
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
  });

  // Filters
  const [leadStatusOptions, setLeadStatusOptions] = useState([]);
  console.log("leadStatusOptions: ", leadStatusOptions);
  const [selectedFilters, setSelectedFilters] = useState(emptyFilterValues);
  const [activeFilterCategory, setActiveFilterCategory] =
    useState("City/Town");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState(emptyFilterValues);

  // Delete Modal
  const [deleteModalState, setDeleteModalState] = useState({
    id: "",
    status: false,
  });

  const [isLoading, setIsLoading] = useState(true);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}healthsy-pharmacy/store-location/list`,
      initialParams: {
        sort,
        searchText,
        selectedValues: selectedFilters,
      },
    });

  useEffect(() => {
    fetchData({
      sort,
      searchText,
      selectedValues: selectedFilters,
      skip: pagination.skip,
      limit: pagination.limit,
    });
  }, [sort, searchText, selectedFilters, pagination.skip, pagination.limit]);

  const deleteHandler = useDeleteHandler(
    "healthsy-pharmacy/store-location",
    fetchData,
    setDeleteModalState
  );

  const handleDelete = (id) => {
    deleteHandler(id);
    setDeleteModalState({ id: "", status: false });
    message.success("Item deleted successfully");
  };

  const handleEdit = (id) => {
    navigate(`/franchise-customers/store-locations/form/${id}`);
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await apiCall(
        `healthsy-pharmacy/store-location/update/${id}`,
        "PATCH",
        { active: !currentStatus },
        null
      );
      fetchData();
      message.success("Status updated successfully");
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      fixed: "left",
      width: 10,
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
    },
    {
      title: "City/Town",
      dataIndex: "city",
      key: "city",
      fixed: "left",
      width: 250,
    },
    {
      title: "Location",
      dataIndex: "town",
      key: "town",
      fixed: "left",
      width: 250,
    },
    {
      title: "Image",
      dataIndex: "photos",
      key: "photos",
      width: 100,
      render: (photos) => {
        if (photos && photos.length > 0) {
          const photoPath = photos[0];
          const imageUrl = photoPath?.startsWith("http")
            ? photoPath
            : new URL(photoPath, process.env.REACT_APP_API_URL).href;

          return (
            <img
              src={imageUrl}
              alt="Location"
              style={{ width: "50px", height: "50px", objectFit: "cover" }}
              onError={(e) => {
                e.target.src = process.env.PUBLIC_URL + "/fallback-image.jpg";
                e.target.alt = "Image not available";
              }}
            />
          );
        }
        return <span>No Image</span>;
      },
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
      width: 150,
      render: (tag) => {
        if (!tag || !tag.status) return "No Tag";
        const chipClass =
          tag.status === "Coming Soon"
            ? "tag-chip coming-soon"
            : "tag-chip open-now";
        return (
          <span className={chipClass}>
            {tag?.number_of_pharmacies} {tag.status}
          </span>
        );
      },
    },
    {
      title: "Status & Actions",
      width: 250,
      render: (text, record) => (
        <StatusComponent
          text={text}
          record={record}
          handleEdit={handleEdit}
          setDeleteModal={setDeleteModalState}
          handleActive={(id) => handleToggleActive(id, record.active)}
          title="store_location"
        />
      ),
    },
  ];

  const handlePageChange = usePageChange(pagination, setPagination);

  // Fetch options for filters
  const fetchAndMap = async (url, mapFn) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}${url}`,
        { noLimit: true }
      );
      return data?.rows?.map(mapFn) || [];
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      return [];
    }
  };

  useEffect(() => {
    if (records) setTimeout(() => setIsLoading(false));
  }, [records]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      const city_town = await fetchAndMap(
        "healthsy-pharmacy/store-location/list",
        (city) => city?.city
      );
      setLeadStatusOptions(city_town);
    };
    fetchFilterOptions();
  }, []);

  const filters = useMemo(
    () => ({
      "City/Town": leadStatusOptions,
      "Registered At": [],
    }),
    // eslint-disable-next-line
    [leadStatusOptions]
  );

  const addStoreLocationButton = {
    id: "addnewstorelocation",
    onClick: () => navigate("/franchise-programme/store-locations/form"),
    style: { width: "240px" },
    label: "Add New Store Location",
  };

  return (
    <>
      <ListPageLayout
        breadcrumbs={
          <AntdBreadcrumbs
            className="healthsyMasterBreadCrumbs pb-0"
            breadcrumbsList={[
              { text: "Franchise programme", link: "" },
              { text: "Store Location", link: "" },
            ]}
          />
        }
        addButton={<FilledButton props={addStoreLocationButton} />}
        titles={<></>}
        filters={
          <DoctorsFilterOptions
            limit={pagination.limit}
            sortName={sortLabel}
            search={searchText}
            setFilterShow={() => setIsFilterModalOpen(true)}
            onChange={debouncedSearch}
            setLimit={(val) =>
              setPagination((prev) => ({
                ...prev,
                limit: val,
                skip: 0,
                currentPage: 1,
              }))
            }
            downloadExcel={false}
            handleSortClick={handleSortClick}
            showSortBy={false}
          />
        }
        tableProps={{
          columns,
          dataSource: records,
          className: "mx-3 pt-2 table-scrollable medicineList_table",
        }}
        totalCount={totalCount}
        pagination={pagination}
        onPageChange={(selected) => handlePageChange({ selected })}
        loader={isLoading}
      >
        {deleteModalState.status && (
          <Deletepopup
            handleDelete={handleDelete}
            id={deleteModalState?.id}
            isCloseModalEnable={true}
            handleClose={() => setDeleteModalState({ id: "", status: false })}
          />
        )}

        <CommonFIlterModal
          visible={isFilterModalOpen}
          onClose={() => setIsFilterModalOpen(false)}
          onApply={async () => {
           await setSelectedFilters(tempFilters);
           await setIsFilterModalOpen(false);
           await setPagination((prev) => ({
              ...prev,
              skip: 0,
              currentPage: 1,
            }));
          }}
          onReset={() => {
            setSelectedFilters(emptyFilterValues);
            setTempFilters(emptyFilterValues);
            setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
            fetchData();
          }}
          filters={filters}
          selectedCategory={activeFilterCategory}
          setSelectedCategory={setActiveFilterCategory}
          selectedValues={tempFilters}
          setSelectedValues={setTempFilters}
        />
        
 
          
            
           
          {/* filters={filters}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedValues={tempSelectedValues}
          setSelectedValues={setTempSelectedValues} */}
       
      </ListPageLayout>
    </>
  );
}
